import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { LoomEmbed } from "~/components/ui/loom-embed";
import { remarkLoomEmbeds } from "~/components/ui/remark-loom-embeds";
import { cn } from "~/lib/utils";

// `dark:prose-invert` covers the elements not pinned to `text-foreground` — most
// visibly `code`, whose default near-black is unreadable on a dark surface.
export const PROSE_CLASSNAME =
  "prose prose-sm max-w-none dark:prose-invert prose-headings:font-semibold prose-headings:text-foreground prose-p:text-foreground prose-li:text-foreground prose-strong:text-foreground prose-em:text-foreground";

type Components = Parameters<typeof ReactMarkdown>[0]["components"];

interface MarkdownPreviewProps {
  children: string;
  className?: string;
  components?: Components;
}

// `loom-embed` is not an HTML element — it is the custom element name the
// remark plugin emits, so the cast is what lets it sit in this map. Same shape
// as the `artifact-ref` mapping in the conversations `Markdown` renderer.
const COMPONENTS = {
  "loom-embed": LoomEmbed,
} as Components;

/**
 * Renders authored Markdown (descriptions, agent instructions, skills) as prose.
 * GFM plus one widget: a Loom link alone on its line becomes an embedded player.
 * Assistant messages use the richer `Markdown` renderer instead — it adds math,
 * highlighted code blocks, and artifact refs that only mean something in a chat.
 *
 * `components` overrides the defaults for a caller whose Markdown needs a
 * different rendering policy — agent-written prose, which links differently
 * from authored prose.
 */
export function MarkdownPreview({ children, className, components }: MarkdownPreviewProps) {
  return (
    <div className={cn(PROSE_CLASSNAME, className)}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkLoomEmbeds]}
        components={{ ...COMPONENTS, ...components }}
      >
        {children}
      </ReactMarkdown>
    </div>
  );
}
